import { memo, useMemo } from 'react';

import { Stack } from '@mui/material';
import { SortingRule } from 'react-table';

import { AccessCentreRole } from 'domains/accessCentre/types';
import Table, { ColumnOption } from 'domains/core/components/Table';
import TruncatedTextWithTooltip from 'domains/core/components/TruncatedTextWithTooltip';
import { SortDirection } from 'models/enums';
import { formatTimestamp } from 'utils';

import RolesTableOptionsCell from './RolesTableOptionsCell';

type RolesTableRow = AccessCentreRole & { lastModifiedSort: Date };

const defaultSortingRules: SortingRule<object>[] = [{ id: 'last_modified_timestamp', desc: true }];

const noopOnSort = (_columnId: string, _direction: SortDirection) => {};

type Props = {
    roles: AccessCentreRole[];
    isLoading?: boolean;
    /** View / Edit / Duplicate in row menu (access_management.manage). Default true. */
    showRoleRowActions?: boolean;
    onSort?: (sortColumn: string, direction: SortDirection) => void;
    /** When set, Duplicate opens this handler instead of navigating to the role builder copy flow. */
    onDuplicateRole?: (role: AccessCentreRole) => void;
};

const RolesTable = ({ roles, isLoading, showRoleRowActions = true, onSort = noopOnSort, onDuplicateRole }: Props) => {
    const data: object[] = useMemo(
        () =>
            roles.map((r) => ({
                ...r,
                lastModifiedSort: new Date(r.last_modified_timestamp ?? 0),
            })),
        [roles]
    );

    const columns: ColumnOption[] = useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: 'name',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => <TruncatedTextWithTooltip value={row.cell.value ?? ''} />,
            },
            {
                id: 'user_count',
                Header: 'No. Of Users',
                accessor: 'user_count',
                align: 'left',
                sortType: 'number',
            },
            {
                id: 'updated_by',
                Header: 'Updated by',
                accessor: 'updated_by',
                align: 'left',
                sortType: 'alphanumeric',
                Cell: (row: any) => <TruncatedTextWithTooltip value={row.cell.value ?? ''} />,
            },
            {
                id: 'last_modified_timestamp',
                Header: 'Date Updated',
                accessor: 'lastModifiedSort',
                sortType: 'datetime',
                Cell: (row: any) => formatTimestamp((row.row.original as RolesTableRow).last_modified_timestamp),
            },
            {
                Header: '',
                accessor: 'id',
                align: 'right',
                disableSortBy: true,
                Cell: (val: any) => (
                    <RolesTableOptionsCell
                        role={val.row.original as AccessCentreRole}
                        showActions={showRoleRowActions}
                        onDuplicateRole={onDuplicateRole}
                    />
                ),
            },
        ],
        [showRoleRowActions, onDuplicateRole]
    );

    return (
        <Stack
            data-testid={isLoading ? 'access-center-roles-table-loading' : 'access-center-roles-table'}
            role={isLoading ? 'progressbar' : 'table'}
            aria-label={isLoading ? 'Loading roles' : 'Roles table'}
        >
            <Table
                columns={columns}
                data={data}
                defaultSortingRules={defaultSortingRules}
                isLoading={isLoading}
                manualSortBy
                onSort={onSort}
                stickyHeader
            />
        </Stack>
    );
};

export default memo(RolesTable);
